import { Link } from 'react-router-dom';
import {
  ArrowLeft, Download, ShieldCheck, Lock, Database, Server, FileCheck2, Eye,
  KeyRound, Fingerprint, Webhook, ScrollText, Ban, Trash2, CalendarX2,
} from 'lucide-react';

const PDF_URL = '/PipeGuide_Security_Architecture_Overview.pdf';

const STACK = [
  { layer: 'Frontend', tech: 'React (SPA)', role: 'Rep live-call screen, manager analytics, admin console' },
  { layer: 'Backend', tech: 'FastAPI (async Python)', role: 'Stateless REST API, horizontally scalable' },
  { layer: 'Database', tech: 'MongoDB', role: 'Multi-tenant document store — orgs, users, transcripts, billing' },
  { layer: 'Hosting', tech: 'Managed Kubernetes', role: 'TLS-terminated ingress, isolated pods, custom domain' },
];

const SERVICES = [
  { name: 'Recall.ai', purpose: 'Meeting bot (Zoom / Teams / Meet / Webex), live transcription, optional read-only calendar sync', data: 'Meeting URL, call audio, transcript; calendar events if connected. No AI training on customer data; calendar events auto-purged after 60 days' },
  { name: 'Deepgram', purpose: 'Live speech-to-text for in-person / phone mode', data: 'Microphone audio stream only' },
  { name: 'OpenAI (GPT-5.2)', purpose: 'Live qualification scoring, suggested questions, post-call summaries', data: 'Transcript text only — API tier, not used for model training' },
  { name: 'Paystack', purpose: 'Subscription billing & usage packs (ZAR)', data: 'Card data handled entirely by Paystack — never touches PipeGuide' },
  { name: 'Google / Microsoft OAuth', purpose: 'SSO sign-in; optional read-only calendar', data: 'Email, name; upcoming event metadata only — never writes to calendars' },
  { name: 'Resend', purpose: 'Transactional email (verification, coaching digests, reminders)', data: 'Recipient email + report content' },
  { name: 'Object storage', purpose: 'Logos & catalogue uploads', data: 'Files only, organisation-scoped' },
];

const CONTROLS = [
  { icon: KeyRound, title: 'Authentication', desc: 'JWT access + refresh tokens in httpOnly secure cookies (XSS-resistant); optional Google SSO; token versioning for instant global session revocation' },
  { icon: Fingerprint, title: 'Passwords & identity', desc: 'bcrypt hashing, 6-digit email verification at signup, disposable-email domains blocked' },
  { icon: Ban, title: 'Brute-force protection', desc: '5 failed attempts triggers a 15-minute lockout per IP + email, with per-endpoint rate limiting across auth, uploads and AI routes' },
  { icon: Lock, title: 'Encryption at rest', desc: 'Third-party credentials encrypted (Fernet / AES-128) before storage; TLS everywhere in transit' },
  { icon: Webhook, title: 'Webhook integrity', desc: 'Payment and bot callbacks verified with HMAC signatures — forged events are rejected' },
  { icon: ScrollText, title: 'Audit logging', desc: 'Administrative and platform actions recorded with actor and timestamp; strict Content-Security-Policy headers' },
];

const FAQ = [
  { q: 'Do you train AI on our data?', a: 'No. All AI providers run on no-training API tiers, and calendar data never reaches AI at all.' },
  { q: 'Can another customer see our data?', a: 'No — organisation-scoped queries are enforced server-side on every endpoint.' },
  { q: 'What happens if we leave?', a: 'Your organisation can be terminated and hard-purged on request; PDF summaries are exportable beforehand.' },
  { q: 'Where does call audio go?', a: 'Streamed for live transcription, transcribed, then discarded — no recordings at rest.' },
  { q: 'Who can start recording a call?', a: 'Only the sales rep, explicitly, per call — and the bot is a visible participant in the meeting.' },
  { q: 'How are payments secured?', a: 'Paystack handles all card data; PipeGuide never stores payment details and verifies every webhook signature.' },
];

export default function SecurityPage() {
  return (
    <div className="min-h-screen bg-white" data-testid="security-page">
      {/* Hero */}
      <div className="bg-[#0A343D] relative overflow-hidden">
        <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-[#0E4E5A] opacity-40 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-40 -left-24 w-96 h-96 rounded-full bg-[#7CD9CC] opacity-10 blur-3xl pointer-events-none" />
        <div className="max-w-5xl mx-auto px-6 py-14 relative">
          <Link to="/login" className="inline-flex items-center gap-1 text-sm text-white/60 hover:text-white mb-8" data-testid="back-to-login">
            <ArrowLeft className="w-4 h-4" /> Back to PipeGuide
          </Link>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-2xl bg-white/10 border border-white/15 flex items-center justify-center">
              <ShieldCheck className="w-6 h-6 text-[#7CD9CC]" />
            </div>
            <span className="text-xs font-bold uppercase tracking-widest text-[#7CD9CC]">Trust & Security</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-white tracking-tight max-w-2xl" style={{ fontFamily: 'var(--font-heading)' }}>
            Security &amp; Architecture
          </h1>
          <p className="text-white/60 text-lg mt-3 max-w-2xl">
            How PipeGuide protects your conversations, isolates your organisation&apos;s data, and governs AI — written for CIOs, CTOs and IT reviewers.
          </p>
          <a
            href={PDF_URL}
            download
            className="inline-flex items-center gap-2 mt-7 h-12 px-6 rounded-xl bg-[#7CD9CC] text-[#0A343D] font-bold text-sm shadow-md transition-transform hover:-translate-y-0.5"
            data-testid="download-security-pdf-btn"
          >
            <Download className="w-4 h-4" /> Download the PDF overview
          </a>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-12 space-y-14">
        {/* Architecture */}
        <section data-testid="security-architecture">
          <div className="flex items-center gap-2 mb-5">
            <Server className="w-5 h-5 text-brand-primary" />
            <h2 className="text-2xl font-bold tracking-tight text-zinc-900" style={{ fontFamily: 'var(--font-heading)' }}>Architecture at a glance</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {STACK.map(s => (
              <div key={s.layer} className="border border-zinc-200 rounded-2xl p-5 bg-zinc-50/50">
                <p className="text-xs font-bold uppercase tracking-widest text-zinc-400">{s.layer}</p>
                <p className="font-semibold text-zinc-900 mt-1">{s.tech}</p>
                <p className="text-xs text-zinc-500 mt-1 leading-relaxed">{s.role}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Connected services */}
        <section data-testid="security-services">
          <div className="flex items-center gap-2 mb-5">
            <Database className="w-5 h-5 text-brand-primary" />
            <h2 className="text-2xl font-bold tracking-tight text-zinc-900" style={{ fontFamily: 'var(--font-heading)' }}>Connected services — and exactly what each one sees</h2>
          </div>
          <div className="overflow-x-auto border border-zinc-200 rounded-2xl">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#0E4E5A] text-white text-left text-xs uppercase tracking-wide">
                  <th className="px-4 py-3">Service</th>
                  <th className="px-4 py-3">Purpose</th>
                  <th className="px-4 py-3">Data exposure</th>
                </tr>
              </thead>
              <tbody>
                {SERVICES.map((s, i) => (
                  <tr key={s.name} className={i % 2 ? 'bg-zinc-50/60' : 'bg-white'}>
                    <td className="px-4 py-3 font-semibold text-zinc-800 whitespace-nowrap">{s.name}</td>
                    <td className="px-4 py-3 text-zinc-600">{s.purpose}</td>
                    <td className="px-4 py-3 text-zinc-600">{s.data}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-4 border-l-4 border-[#7CD9CC] bg-[#f2fbf9] rounded-r-xl p-5 text-sm text-zinc-700 leading-relaxed">
            <span className="font-bold text-[#0A343D]">AI data governance:</span> calendar / Workspace data is architecturally isolated from AI — it is never included
            in any AI model input (code-enforced, and publicly disclosed in our <Link to="/privacy" className="text-brand-primary underline">Privacy Policy</Link> per
            Google&apos;s Limited Use requirements). No call recordings are stored — only transcripts and summaries, owned by your organisation.
          </div>
        </section>

        {/* Isolation */}
        <section data-testid="security-isolation">
          <div className="flex items-center gap-2 mb-5">
            <Eye className="w-5 h-5 text-brand-primary" />
            <h2 className="text-2xl font-bold tracking-tight text-zinc-900" style={{ fontFamily: 'var(--font-heading)' }}>Multi-tenancy &amp; data isolation</h2>
          </div>
          <ul className="space-y-3 text-sm text-zinc-700 leading-relaxed">
            <li className="flex gap-3"><span className="text-[#7CD9CC] font-bold shrink-0">—</span><span>Every record carries an organisation ID; <strong>every API query is scoped server-side</strong> to the authenticated user&apos;s organisation — cross-tenant access is impossible by construction, independently audited across all endpoints</span></li>
            <li className="flex gap-3"><span className="text-[#7CD9CC] font-bold shrink-0">—</span><span>Role-based access: Admin (configuration, view-only on calls) · Manager (department-scoped analytics) · Sales rep (own calls only)</span></li>
            <li className="flex gap-3"><span className="text-[#7CD9CC] font-bold shrink-0">—</span><span>Compound database indexes on the tenant key keep isolation performant at scale</span></li>
          </ul>
        </section>

        {/* Controls */}
        <section data-testid="security-controls">
          <div className="flex items-center gap-2 mb-5">
            <Lock className="w-5 h-5 text-brand-primary" />
            <h2 className="text-2xl font-bold tracking-tight text-zinc-900" style={{ fontFamily: 'var(--font-heading)' }}>Security controls</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {CONTROLS.map(c => (
              <div key={c.title} className="border border-zinc-200 rounded-2xl p-5">
                <c.icon className="w-5 h-5 text-brand-primary mb-3" />
                <p className="font-semibold text-zinc-900 text-sm">{c.title}</p>
                <p className="text-xs text-zinc-500 mt-1.5 leading-relaxed">{c.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Lifecycle */}
        <section data-testid="security-lifecycle">
          <div className="flex items-center gap-2 mb-5">
            <FileCheck2 className="w-5 h-5 text-brand-primary" />
            <h2 className="text-2xl font-bold tracking-tight text-zinc-900" style={{ fontFamily: 'var(--font-heading)' }}>Data lifecycle &amp; compliance (POPIA-aware)</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="border border-zinc-200 rounded-2xl p-5">
              <Trash2 className="w-5 h-5 text-brand-primary mb-3" />
              <p className="font-semibold text-zinc-900 text-sm">Clean offboarding</p>
              <p className="text-xs text-zinc-500 mt-1.5 leading-relaxed">Suspend → terminate (configurable retention window) → hard purge with full cascade across all collections. Irreversible deletion requires typed confirmation.</p>
            </div>
            <div className="border border-zinc-200 rounded-2xl p-5">
              <CalendarX2 className="w-5 h-5 text-brand-primary mb-3" />
              <p className="font-semibold text-zinc-900 text-sm">Revocable access</p>
              <p className="text-xs text-zinc-500 mt-1.5 leading-relaxed">Calendar access is revocable in-app at any time; synced events are deleted on disconnect. No call recordings at rest — transcripts and summaries only.</p>
            </div>
            <div className="border border-zinc-200 rounded-2xl p-5">
              <ScrollText className="w-5 h-5 text-brand-primary mb-3" />
              <p className="font-semibold text-zinc-900 text-sm">Transparent processing</p>
              <p className="text-xs text-zinc-500 mt-1.5 leading-relaxed">All sub-processors disclosed publicly (<Link to="/sub-processors" className="text-brand-primary underline">see list</Link>), including the Google Limited Use compliance statement.</p>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section data-testid="security-faq">
          <h2 className="text-2xl font-bold tracking-tight text-zinc-900 mb-5" style={{ fontFamily: 'var(--font-heading)' }}>Common CIO / CTO questions</h2>
          <div className="space-y-3">
            {FAQ.map(f => (
              <div key={f.q} className="border border-zinc-200 rounded-2xl p-5">
                <p className="font-semibold text-zinc-900 text-sm">{f.q}</p>
                <p className="text-sm text-zinc-600 mt-1.5 leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="bg-[#0A343D] rounded-2xl p-8 sm:p-10 relative overflow-hidden" data-testid="security-cta">
          <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-[#7CD9CC] opacity-10 blur-3xl pointer-events-none" />
          <h2 className="text-2xl font-bold text-white tracking-tight relative" style={{ fontFamily: 'var(--font-heading)' }}>Need more for your security review?</h2>
          <p className="text-white/60 text-sm mt-2 max-w-xl relative">
            Download the PDF for your proposal pack, or ask us to walk your IT stakeholders through a live architecture review and complete your security questionnaire.
          </p>
          <div className="flex flex-wrap gap-3 mt-6 relative">
            <a href={PDF_URL} download className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#7CD9CC] text-[#0A343D] font-bold text-sm transition-transform hover:-translate-y-0.5" data-testid="cta-download-pdf-btn">
              <Download className="w-4 h-4" /> Download PDF overview
            </a>
            <Link to="/privacy" className="inline-flex items-center h-11 px-5 rounded-xl border border-white/20 text-white font-medium text-sm hover:bg-white/5 transition-colors" data-testid="cta-privacy-link">
              Read our Privacy Policy
            </Link>
          </div>
        </section>

        <p className="text-xs text-zinc-400 text-center pb-4">PipeGuide (Pty) · Last updated June 2026</p>
      </div>
    </div>
  );
}
